import { useEffect } from 'react';
import useSWR from 'swr';
import {
  X, Loader2, User, MapPin, Phone, Mail, Repeat,
  ShoppingBag, Calendar, DollarSign, ExternalLink,
} from 'lucide-react';
import { fetchAdminCustomerDetail } from '@/lib/api';

const AUD = (n) =>
  `$${(Number(n) || 0).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso.slice(0, 10); }
};

const fmtRenewal = (unix) => {
  if (!unix) return '—';
  try {
    return new Date(Number(unix) * 1000).toLocaleDateString('en-AU', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return String(unix); }
};

const STATUS_PILL = {
  paid:     'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  pending:  'bg-amber-500/15 text-amber-300 border-amber-500/30',
  failed:   'bg-red-500/15 text-red-300 border-red-500/30',
  expired:  'bg-white/5 text-white/40 border-white/10',
  refunded: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
};

const SUB_STATUS_PILL = {
  active:   'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  trialing: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  past_due: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  canceled: 'bg-white/5 text-white/40 border-white/10',
};

/**
 * Slide-in side drawer showing every past order + Stripe subscription + a
 * quick "email customer" action. Fetches full detail on open, keyed by email.
 */
export default function CustomerDetailDrawer({ email, onClose }) {
  const { data, isLoading, error } = useSWR(
    email ? ['admin-customer-detail', email] : null,
    () => fetchAdminCustomerDetail(email),
  );

  // ESC to close + prevent background scroll while open
  useEffect(() => {
    if (!email) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [email, onClose]);

  if (!email) return null;

  const emailSubject = encodeURIComponent(`The Meat Works · ${data?.name || 'Order enquiry'}`);
  const emailBody = encodeURIComponent(
    `Hi ${data?.name || 'there'},\n\nThanks for shopping with The Meat Works — following up on your recent order(s).\n\n`
  );
  const mailtoHref = `mailto:${email}?subject=${emailSubject}&body=${emailBody}`;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        data-testid="customer-drawer-backdrop"
        className="fixed inset-0 bg-black/60 z-50 backdrop-blur-sm"
      />
      {/* Drawer */}
      <aside
        data-testid="customer-drawer"
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[520px] bg-[#080808] border-l border-white/10 flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/10 flex items-start justify-between gap-4 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-11 h-11 border border-white/10 flex items-center justify-center text-white/50 shrink-0">
              <User size={18} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.28em] text-white/45 mb-1">Customer</div>
              <h3 className="font-display text-xl text-white truncate">{data?.name || '—'}</h3>
              <div className="text-xs text-white/50 truncate">{email}</div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="customer-drawer-close"
            className="text-white/50 hover:text-white transition-colors shrink-0"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-white/40" /></div>
          )}
          {error && (
            <div className="p-6 text-white/50 text-sm">{error?.response?.data?.detail || 'Could not load customer.'}</div>
          )}
          {data && (
            <div className="p-6 space-y-8">
              {/* Aggregate stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border border-white/10 p-3">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-white/45">Lifetime</div>
                  <div className="mt-1.5 font-display text-lg text-white tabular-nums">{AUD(data.lifetime_spend)}</div>
                </div>
                <div className="border border-white/10 p-3">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-white/45">Orders</div>
                  <div className="mt-1.5 font-display text-lg text-white tabular-nums">
                    {data.paid_orders_count}<span className="text-white/35 text-sm">/{data.orders_count}</span>
                  </div>
                </div>
                <div className="border border-white/10 p-3">
                  <div className="text-[9px] uppercase tracking-[0.24em] text-white/45">Since</div>
                  <div className="mt-1.5 text-xs text-white/85">{fmtDate(data.first_order_at)}</div>
                </div>
              </div>

              {/* Contact card */}
              <div className="border border-white/10 divide-y divide-white/5">
                <div className="px-4 py-3 flex items-center gap-3 text-sm">
                  <Mail size={13} className="text-white/40 shrink-0" />
                  <a href={mailtoHref} className="text-white/80 hover:text-white truncate">{email}</a>
                </div>
                {data.phone && (
                  <div className="px-4 py-3 flex items-center gap-3 text-sm">
                    <Phone size={13} className="text-white/40 shrink-0" />
                    <a href={`tel:${data.phone}`} className="text-white/80 hover:text-white">{data.phone}</a>
                  </div>
                )}
                {data.suburbs?.length > 0 && (
                  <div className="px-4 py-3 flex items-center gap-3 text-sm">
                    <MapPin size={13} className="text-white/40 shrink-0" />
                    <span className="text-white/80">{data.suburbs.join(' · ')}</span>
                  </div>
                )}
              </div>

              {/* Email action */}
              <a
                href={mailtoHref}
                data-testid="customer-drawer-email"
                style={{ color: '#fff' }}
                className="inline-flex items-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-[11px] uppercase tracking-[0.22em] px-5 py-3 transition-colors"
              >
                <Mail size={13} /> Email customer
              </a>

              {/* Subscriptions */}
              {data.subscriptions?.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">
                    <Repeat size={11} /> Subscriptions · {data.subscriptions.length}
                  </div>
                  <div className="border border-white/10 divide-y divide-white/5">
                    {data.subscriptions.map((s) => (
                      <div key={s.subscription_id} className="px-4 py-3 flex items-center gap-3 text-sm">
                        <span className={`inline-flex text-[9px] uppercase tracking-[0.22em] border px-2 py-0.5 ${SUB_STATUS_PILL[s.status] || SUB_STATUS_PILL.canceled}`}>
                          {s.status}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] text-white/40 tabular-nums truncate">{s.subscription_id}</div>
                          {s.cancel_at_period_end && (
                            <div className="text-[10px] text-amber-400 uppercase tracking-[0.22em]">Cancels at period end</div>
                          )}
                        </div>
                        <div className="text-[11px] uppercase tracking-[0.22em] text-white/50 shrink-0 inline-flex items-center gap-1">
                          <Calendar size={11} /> {fmtRenewal(s.current_period_end)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order history */}
              <div>
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-white/50 mb-3">
                  <ShoppingBag size={11} /> Order history · {data.orders.length}
                </div>
                <div className="border border-white/10 divide-y divide-white/5">
                  {data.orders.map((o) => (
                    <div key={o.id} className="px-4 py-3.5 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white/45 tabular-nums">{fmtDate(o.created_at)}</div>
                          <div className="text-[10px] text-white/35 tabular-nums truncate">{o.id}</div>
                        </div>
                        <span className={`inline-flex text-[9px] uppercase tracking-[0.22em] border px-2 py-0.5 ${STATUS_PILL[o.payment_status] || STATUS_PILL.pending}`}>
                          {o.payment_status || 'pending'}
                        </span>
                        {o.is_subscription && (
                          <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] text-emerald-400 border border-emerald-500/30 px-2 py-0.5">
                            <Repeat size={9} /> Sub
                          </span>
                        )}
                        <div className="font-display text-white tabular-nums text-right">{AUD(o.total)}</div>
                      </div>
                      {o.items?.length > 0 && (
                        <div className="text-[11px] text-white/50 leading-relaxed pl-1">
                          {o.items.slice(0, 4).map((it, i) => (
                            <span key={i}>
                              {it.name} <span className="text-white/30 tabular-nums">×{it.quantity}{it.unit === 'kg' ? 'kg' : ''}</span>
                              {i < Math.min(o.items.length - 1, 3) && <span className="text-white/25"> · </span>}
                            </span>
                          ))}
                          {o.items.length > 4 && <span className="text-white/30"> · +{o.items.length - 4} more</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </aside>
      {/* silencer for unused imports */}
      <DollarSign className="hidden" />
      <ExternalLink className="hidden" />
    </>
  );
}
